import { CSSProperties } from 'react';
import { ACCENT_BUTTON, COLORS, FONTS, glass, META } from '../../globalStyles';

// Launcher + panel share one frosted surface so they read as one family.
const VAE_SURFACE: CSSProperties = {
  ...glass('var(--vae-glass)', 11),
  border: `1px solid ${COLORS.borderStrong}`,
  borderRadius: 0,
};

export const styles: Record<string, CSSProperties> = {
  // Fixed wrapper — holds the launcher in the bottom-right corner. (The button
  // itself can't be fixed: ReflectionButton force-applies position:relative.)
  launcherWrap: {
    position: 'fixed',
    right: '1.5rem',
    bottom: '1.5rem',
    zIndex: 120,
  },
  // Floating launcher (VAE's avatar = the signature mark). Vertical tab.
  launcher: {
    ...VAE_SURFACE,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.6rem 0.7rem',
    color: COLORS.text,
    cursor: 'pointer',
  },
  // "ASK" / mark / "VAE" — the mark sandwiched, centered, between the two words.
  launcherStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.4rem',
  },
  launcherLabel: {
    ...META,
    fontSize: '0.72rem',
    color: COLORS.text,
    textAlign: 'center',
    lineHeight: 1,
  },

  // The chat panel.
  panel: {
    position: 'fixed',
    right: '1.5rem',
    bottom: '1.5rem',
    zIndex: 140,
    width: 'min(380px, calc(100vw - 2rem))',
    height: 'min(560px, calc(100dvh - 3rem))',
    display: 'flex',
    flexDirection: 'column',
    ...VAE_SURFACE,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.7rem',
    padding: '0.9rem 1rem',
    borderBottom: `1px solid ${COLORS.border}`,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
    marginRight: 'auto',
  },
  headerName: {
    ...META,
    fontSize: '0.82rem',
    color: COLORS.text,
  },
  headerSub: {
    ...META,
    fontSize: '0.58rem',
    color: COLORS.faint,
  },
  close: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    background: 'none',
    border: 'none',
    color: COLORS.muted,
    cursor: 'pointer',
    padding: 0,
  },

  // Transcript. Each turn is a META eyebrow naming the speaker + the body
  // beneath it — the site's section-eyebrow language applied to dialogue.
  // Speakers are told apart by the eyebrow, not by coloured/aligned bubbles.
  messages: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.15rem',
    padding: '1.1rem 1rem',
  },
  turn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
  },
  eyebrow: {
    ...META,
    fontSize: '0.58rem',
    letterSpacing: '0.22em',
  },
  eyebrowVae: { color: COLORS.accent },
  eyebrowUser: { color: COLORS.faint },
  eyebrowNote: { color: COLORS.faint },
  body: {
    fontSize: '0.9rem',
    lineHeight: 1.62,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    color: COLORS.text,
  },
  bodyNote: {
    color: COLORS.faint,
    fontStyle: 'italic',
  },
  // Console-style "thinking" cue: a blinking accent block after VAE's eyebrow.
  caret: {
    display: 'inline-block',
    width: '0.5em',
    height: '1.05em',
    background: COLORS.accent,
    verticalAlign: '-0.15em',
  },

  // Composer.
  form: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '0.5rem',
    padding: '0.75rem',
    borderTop: `1px solid ${COLORS.border}`,
  },
  input: {
    flex: 1,
    minWidth: 0,
    resize: 'none',
    minHeight: '42px', // matches the send button's height on a single line
    maxHeight: '110px',
    background: COLORS.bg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 0,
    color: COLORS.text,
    fontFamily: FONTS.body,
    fontSize: '0.9rem',
    lineHeight: 1.4,
    padding: '0.6rem 0.7rem',
  },
  // Squared accent action, echoing the hero's "→".
  send: {
    ...ACCENT_BUTTON,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '42px',
    height: '42px',
    fontSize: '1.1rem',
    lineHeight: 1,
  },
};
